/** One repeatable bay of the loading yard's galvanized perimeter. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const POST_STEEL = 0x848b90;
const WIRE_STEEL = 0x9aa1a6;

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function wire(length: number, material: THREE.Material, name: string): THREE.Mesh {
  return part(new THREE.CylinderGeometry(0.006, 0.006, length, 6), material, name);
}

/** The far post is placed separately so a run has exactly one post at each joint. */
export function buildFencePost(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'perimeter-fence-post';
  const steel = surface('metal', POST_STEEL);
  // The prescribed square box post already has the required flat horizontal cap;
  // a second cap mesh would spend triangles on a detail this silhouette cannot show.
  const post = part(new THREE.BoxGeometry(0.08, 2.15, 0.08), steel, 'steel-post');
  post.position.y = 1.075;
  group.add(post);
  return group;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'perimeter-fence';
  const steel = surface('metal', POST_STEEL);
  const wireSteel = surface('metal', WIRE_STEEL);

  const post = buildFencePost();
  post.position.z = -1.20;
  group.add(post);

  // A bay owns only its -Z post: owning both ends would overlap identical posts
  // at every instanced joint and z-fight. The room adds buildFencePost() once at
  // the far end, so the deliberately Z-asymmetric local bound is complete, not cheap.
  for (let index = 0; index < 12; index += 1) {
    const vertical = wire(1.92, wireSteel, `wire-vertical-${index}`);
    vertical.position.set(0, 1.06, -1.10 + index * 0.20);
    group.add(vertical);
  }
  for (const y of [0.35, 1.10, 1.85]) {
    const horizontal = wire(2.40, wireSteel, `wire-horizontal-${y.toFixed(2)}`);
    horizontal.rotation.x = Math.PI / 2;
    horizontal.position.set(0, y, 0);
    group.add(horizontal);
  }
  const topRail = part(new THREE.CylinderGeometry(0.02, 0.02, 2.40, 6), steel, 'top-rail');
  topRail.rotation.x = Math.PI / 2;
  topRail.position.set(0, 2.02, 0);
  group.add(topRail);
  const tensionWire = wire(2.40, wireSteel, 'bottom-tension-wire');
  tensionWire.rotation.x = Math.PI / 2;
  tensionWire.position.set(0, 0.10, 0);
  group.add(tensionWire);
  return group;
}

export const perimeterFenceAsset: AssetDefinition = {
  id: 'perimeter-fence',
  label: 'Perimeter fence',
  description: 'Galvanized post-and-lattice perimeter fence bay.',
  // 0.08, not the 0.10 first declared: the post section IS the bay's whole
  // thickness — there is no rail standing proud of it — and a declared 0.10
  // fails the contract's 15 % band at 0.80.
  footprint: { width: 0.08, height: 2.15, length: 2.44 },
  create: build,
};
